import { Box, Flex, Table, Text } from '@chakra-ui/react'
import { ChartSpline } from 'lucide-react'
import React, { useMemo } from 'react'

import { useReportingI18n } from '../../../i18n'
import type { QueueCaller, QueueDashboardSettings } from '../types'
import CallerRow from './CallerRow'
import { SettingsControls } from './SettingsControls'

export interface CallersTableProps {
  callers: QueueCaller[]
  holdAlertThreshold: number
  isLoading: boolean
  settings?: QueueDashboardSettings
  onSettingsChange?: (settings: Partial<QueueDashboardSettings>) => void
}

const Thead = Table.Header
const Tbody = Table.Body
const Tr = Table.Row
const Th = Table.ColumnHeader

const tableHeader = (title: string, shrink?: boolean) => {
  return (
    <Th
      padding="12px 16px"
      bg="gray.subtle"
      color="fg.default"
      fontSize="0.75rem"
      fontWeight="600"
      textTransform="none"
      width={shrink ? '1%' : undefined}
      whiteSpace={shrink ? 'nowrap' : undefined}
    >
      {title}
    </Th>
  )
}

const EmptyState: React.FC = () => {
  const { text } = useReportingI18n()

  return (
    <Flex
      direction="column"
      alignItems="center"
      justifyContent="center"
      padding="48px"
      color="fg.muted"
      data-testid="callers-table-empty-state"
    >
      <Flex
        alignItems="center"
        justifyContent="center"
        width="80px"
        height="80px"
        borderRadius="full"
        bg="bg.muted"
        mb="16px"
      >
        <Box
          as={ChartSpline}
          width="36px"
          height="36px"
          color="blue.focusRing"
        />
      </Flex>
      <Text fontSize="1rem" fontWeight="600" color="fg.default">
        {text(
          'queueDashboard.callersTable.emptyState.title',
          'No information found',
        )}
      </Text>
      <Text fontSize="0.875rem" mt="4px" color="fg.muted">
        {text(
          'queueDashboard.callersTable.emptyState.description',
          'There is currently no information showing for the current Queue',
        )}
      </Text>
    </Flex>
  )
}

const CallersTable: React.FC<CallersTableProps> = ({
  callers,
  holdAlertThreshold,
  isLoading,
  settings,
  onSettingsChange,
}) => {
  const { text } = useReportingI18n()
  const sortedCallers = useMemo(() => {
    return [...callers].sort((a, b) => {
      const aIsWaiting = a.state === 'Waiting' || a.state === 'Trying'
      const bIsWaiting = b.state === 'Waiting' || b.state === 'Trying'

      // Waiting calls always sort to the top
      if (aIsWaiting && !bIsWaiting) return -1
      if (!aIsWaiting && bIsWaiting) return 1

      // Within the same group, sort by longest wait time first (highest score)
      return b.score - a.score
    })
  }, [callers])

  return (
    <Box
      bg="bg.panel"
      borderRadius="8px"
      border="1px solid"
      borderColor="border.default"
      overflow="hidden"
      data-testid="callers-table"
      flex="1"
    >
      <Flex
        direction="column"
        justifyContent="space-between"
        alignItems="flex-start"
        gap="12px"
        padding="16px"
        css={{
          // Container query: switch to row layout when container is wide enough
          '@container queue-dashboard (min-width: 950px)': {
            flexDirection: 'row',
            alignItems: 'center',
            gap: '16px',
          },
        }}
      >
        <Text fontSize="1rem" fontWeight="700" color="fg.default">
          {text('queueDashboard.callersTable.title', 'Inbound Queue')}
        </Text>
        {settings && onSettingsChange && (
          <SettingsControls
            settings={settings}
            onSettingsChange={onSettingsChange}
            compact
          />
        )}
      </Flex>

      {!isLoading && sortedCallers.length === 0 ? (
        <EmptyState />
      ) : (
        <Box padding="16px">
          <Box
            borderRadius="md"
            overflow="hidden"
            border="1px solid"
            borderColor="border.default"
            maxHeight="500px"
            overflowY="auto"
          >
            <Table.Root variant="line" size="sm">
              <Thead>
                <Tr>
                  {tableHeader(
                    text(
                      'queueDashboard.callersTable.callerNumber',
                      'Caller #',
                    ),
                  )}
                  {tableHeader(
                    text(
                      'queueDashboard.callersTable.callerName',
                      'Caller Name',
                    ),
                  )}
                  {tableHeader(
                    text(
                      'queueDashboard.callersTable.callTime',
                      'Call / Wait time',
                    ),
                  )}
                  {tableHeader(
                    text('queueDashboard.callersTable.status', 'Status'),
                    true,
                  )}
                </Tr>
              </Thead>
              <Tbody>
                {sortedCallers.map((caller, index) => (
                  <CallerRow
                    key={caller.uuid}
                    caller={caller}
                    holdAlertThreshold={holdAlertThreshold}
                    index={index}
                  />
                ))}
              </Tbody>
            </Table.Root>
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default CallersTable
